import { NgTemplateOutlet } from "@angular/common";
import {
    Component,
    computed,
    effect,
    ElementRef,
    forwardRef,
    inject,
    input,
    Renderer2,
    RendererStyleFlags2,
    ChangeDetectionStrategy,
} from "@angular/core";
import { Grid } from "./s25.virtual.grid";
import { GridUtil } from "./s25.virtual.grid.util";
import { S25VirtualGridComponent } from "./s25.virtual.grid.component";

const ARROW_STEPS: Record<string, readonly [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowUp: [0, -1],
    ArrowRight: [1, 0],
    ArrowDown: [0, 1],
};

const SHIFT_ARROW_STEPS: Record<string, readonly [number, number]> = {
    ArrowLeft: [-3, 0],
    ArrowUp: [0, -2],
    ArrowRight: [3, 0],
    ArrowDown: [0, 2],
};

/**
 * A single tile of the virtual grid. One component instance per visible entry, so each tile's signal
 * reads land on its own reactive consumer instead of piling onto S25VirtualGridComponent.
 *
 * Host bindings cannot be used for anything reactive here: Angular runs host bindings while refreshing
 * the *declaring* view, and the embedded views created by `*virtualGrid` have no consumer of their own,
 * so every tile would register its producers on the grid again. The tile's own template and the
 * `applyTileState` effect both have a consumer, so reads stay local to the tile.
 */
@Component({
    selector: "s25-ng-virtual-grid-item",
    standalone: true,
    imports: [NgTemplateOutlet],
    host: {
        class: "grid--item",
        "(focus)": "onFocus()",
        "(mousedown)": "onMousedown($event)",
        "(keydown)": "onKeydown($event)",
        "(blur)": "onKeyboardPutDown()",
    },
    styles: `
        :host {
            --item-full-left: 0%;
            --item-full-top: 0%;
            --item-full-width: 100%;
            --item-full-height: 100%;
            --item-full-clip-top: -100vmax;
            --item-full-clip-right: -100vmax;
            --item-full-clip-bottom: -100vmax;
            --item-full-clip-left: -100vmax;
        }

        .grid--item-full {
            position: absolute;
            left: var(--item-full-left);
            top: var(--item-full-top);
            width: var(--item-full-width);
            height: var(--item-full-height);
            clip-path: inset(
                var(--item-full-clip-top) var(--item-full-clip-right) var(--item-full-clip-bottom)
                    var(--item-full-clip-left)
            );
            container-type: inline-size;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        @if (position()) {
            <div class="grid--item-full">
                @if (itemProjection(); as projection) {
                    @if (itemProjectionTemplate(); as itemTmpl) {
                        <ng-container
                            [ngTemplateOutlet]="itemTmpl"
                            [ngTemplateOutletContext]="{
                                item: projection.source,
                                projection: projection,
                                dragging: projection.dragging(),
                                moving: projection.moving(),
                            }"
                        />
                    } @else {
                        <div>{{ projection.source.id }}</div>
                    }
                } @else if (decorationEntry(); as decoration) {
                    @if (decorationTemplate(); as decorationTmpl) {
                        <ng-container
                            [ngTemplateOutlet]="decorationTmpl"
                            [ngTemplateOutletContext]="{ decoration: decoration }"
                        />
                    }
                }
            </div>
        }
    `,
})
export class S25VirtualGridItemComponent {
    readonly entry = input.required<Grid.RenderEntry>();

    private readonly grid = inject<S25VirtualGridComponent<any, any, any, any>>(
        forwardRef(() => S25VirtualGridComponent),
    );
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

    readonly itemProjection = computed(() => {
        const entry = this.entry();
        return entry.type === "item-projection" ? entry : undefined;
    });
    readonly decorationEntry = computed(() => {
        const entry = this.entry();
        return entry.type === "decoration" ? entry : undefined;
    });
    readonly itemProjectionTemplate = computed(() => this.grid.itemProjectionTemplate());
    readonly decorationTemplate = computed(() => this.grid.decorationTemplate());

    readonly position = computed(() => this.entry().clippedPosition());
    private readonly window = computed(() => this.entry().window());
    private readonly interactive = computed(() => this.grid.isEntryInteractive(this.entry()));
    private readonly draggable = computed(() => this.grid.isEntryDraggable(this.entry()));
    private readonly dataId = computed(() => {
        const entry = this.entry();
        return String(entry.type === "item-projection" ? entry.source.id : entry.key);
    });
    private readonly role = computed(() => {
        const entry = this.entry();
        return entry.type === "item-projection" ? entry.role : null;
    });
    private readonly a11yRole = computed(() => (this.entry().type === "item-projection" ? "group" : null));

    /** The geometry currently written to the element, so a move knows where it is flying from. */
    private appliedPosition?: Grid.Rect;
    private runningMove?: Animation;

    constructor() {
        effect(() => this.applyTileState());
        effect(() => this.applyWindow());
    }

    onFocus() {
        this.grid.onEntryFocused(this.entry());
    }

    onMousedown(event: MouseEvent) {
        const entry = this.entry();
        if (entry.type !== "item-projection") return;
        this.grid.onItemMousedown(event, entry);
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.grid.onItemKeyboardPutDown();
            return;
        }
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            const entry = this.entry();
            if (entry.type !== "item-projection") return;
            this.grid.onItemEnter(event, this.elementRef.nativeElement, entry);
            return;
        }
        const step = event.shiftKey ? SHIFT_ARROW_STEPS[event.key] : ARROW_STEPS[event.key];
        if (!step) return;
        this.grid.onItemKeyboardDrag(event, step[0], step[1]);
    }

    onKeyboardPutDown() {
        this.grid.onItemKeyboardPutDown();
    }

    private applyTileState() {
        const position = this.position();
        if (!position) return;

        const element = this.elementRef.nativeElement;
        const entry = this.entry();
        const interactive = this.interactive();

        const previousPosition = this.appliedPosition;
        this.appliedPosition = position;

        this.setPercent(element, "--item-width", position.width);
        this.setPercent(element, "--item-left", position.left);
        this.setPercent(element, "--item-top", position.top);
        this.setPercent(element, "--item-height", position.height);

        // A tile that has never been placed has nowhere to fly from, and an unchanged position means
        // an unrelated part of the tile state changed, so there is no move to play.
        const moved = previousPosition && !GridUtil.positionsEqual(previousPosition, position);
        if (entry.moving() && moved) {
            // A move still in flight would compose with the new one instead of being replaced
            this.runningMove?.cancel();
            this.runningMove = GridUtil.flipElement(
                element,
                previousPosition,
                position,
                this.containingBlock(element),
                S25VirtualGridComponent.ITEM_MOVE_DURATION,
            );
        }
        this.toggleClass(element, "unDraggable", !this.draggable());
        this.toggleClass(element, "dragging", entry.dragging());
        this.toggleClass(element, "moving", entry.moving());
        this.toggleClass(element, "noInteraction", !interactive);
        this.toggleClass(element, "broughtToFront", entry.broughtToFront());

        this.setAttribute(element, "data-id", this.dataId());
        this.setAttribute(element, "data-render-key", entry.key);
        this.setAttribute(element, "data-role", this.role());
        this.setAttribute(element, "aria-label", entry.ariaLabel());
        this.setAttribute(element, "role", this.a11yRole());
        this.renderer.setProperty(element, "tabIndex", interactive ? 0 : -1);
    }

    /**
     * The box the tile's geometry percentages resolve against. For an absolutely positioned tile
     * that is its `offsetParent`, whose own layout is independent of the tile, so reading it back
     * is not subject to the tile's pending style writes or move transform.
     */
    private containingBlock(element: HTMLElement) {
        const reference = (element.offsetParent ?? element.parentElement) as HTMLElement | null;
        if (!reference) return { width: 0, height: 0 };
        return { width: reference.clientWidth, height: reference.clientHeight };
    }

    /**
     * ANG-7906: Size the inner item to the unclipped rect and clip only the sides that were cut,
     * so overhang (time bubbles, focus rings) still escapes the open sides.
     */
    private applyWindow() {
        const itemWindow = this.window();
        const element = this.elementRef.nativeElement;
        const { full, clipped } = itemWindow;
        this.setPercent(element, "--item-full-left", full.left);
        this.setPercent(element, "--item-full-top", full.top);
        this.setPercent(element, "--item-full-width", full.width);
        this.setPercent(element, "--item-full-height", full.height);
        this.setClip(element, "--item-full-clip-left", clipped.left, -full.left, full.width);
        this.setClip(element, "--item-full-clip-right", clipped.right, full.left + full.width - 100, full.width);
        this.setClip(element, "--item-full-clip-top", clipped.top, -full.top, full.height);
        this.setClip(element, "--item-full-clip-bottom", clipped.bottom, full.top + full.height - 100, full.height);
    }

    private setPercent(element: HTMLElement, property: string, value: number | undefined) {
        if (value == null) return this.renderer.removeStyle(element, property, RendererStyleFlags2.DashCase);
        this.renderer.setStyle(element, property, `${value}%`, RendererStyleFlags2.DashCase);
    }

    private setClip(element: HTMLElement, property: string, isClipped: boolean, overflow: number, fullSize: number) {
        const value = !isClipped || !fullSize ? "-100vmax" : `${(overflow / fullSize) * 100}%`;
        this.renderer.setStyle(element, property, value, RendererStyleFlags2.DashCase);
    }

    private toggleClass(element: HTMLElement, className: string, present: boolean) {
        if (present) this.renderer.addClass(element, className);
        else this.renderer.removeClass(element, className);
    }

    private setAttribute(element: HTMLElement, name: string, value: string | null | undefined) {
        if (value == null) return this.renderer.removeAttribute(element, name);
        this.renderer.setAttribute(element, name, value);
    }
}
